import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, formatAxisTick, naturalColor } from '../ui/chartFormat';

/*
 * Generic stacked daily bars: one bar per calendar day, segmented by an
 * arbitrary set of keys. Used for Spain's "coupons per day per brand" (keys =
 * brands) and "participants per day by space" (keys = Stadium/Club/Kiosk).
 *
 * The caller supplies `keys` (the stack segments, in stack order) and a daily
 * `series` of {date, counts}. Keys may carry their own colour; missing colours
 * fall back to a small rotating palette.
 *
 * Tick density mirrors DailyBars — ~8 x-axis labels so a ~57-day window stays
 * legible. The tooltip lists only the segments with a non-zero count that day,
 * sorted desc, so a wide stack doesn't produce an unreadable tooltip.
 */
type DailyStack = { series: Array<{ date: string; counts: Record<string, number> }> };
type StackKey = { key: string; label: string; color?: string };

type Props = {
  data: DailyStack;
  keys: StackKey[];
  /** Recharts stackId — only matters if two stacks share a chart (they don't here). */
  stackId?: string;
  /**
   * Draw the series SIDE BY SIDE instead of stacked. Use it whenever the series
   * overlap rather than partition — e.g. rewards awarded vs claimed, where a
   * claimed reward was also awarded: stacking those would show a total that
   * does not exist.
   */
  grouped?: boolean;
  height?: number;
};

function formatDayLabel(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

const numFmt = new Intl.NumberFormat('en-US');

type TooltipEntry = { name?: string; value?: number; color?: string };

function StackTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;
  const items = payload.filter((p) => (p.value ?? 0) > 0).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  if (items.length === 0) return null;
  return (
    <div style={{ background: 'var(--paper)', border: '1.5px solid var(--line)', borderRadius: 6, fontSize: 12, padding: '6px 10px' }}>
      <div style={{ color: 'var(--ink-soft)', marginBottom: 4 }}>{label}</div>
      {items.map((p) => (
        <div key={p.name} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flex: '0 0 auto' }} />
          <span style={{ flex: 1 }}>{p.name}</span>
          <span className="mono">{numFmt.format(p.value ?? 0)}</span>
        </div>
      ))}
    </div>
  );
}

export function StackedDailyBars({ data, keys, stackId = 'stack', grouped = false, height = 300 }: Props) {
  // Flatten to Recharts rows: { name: '25/05', <key>: count, ... }. Every stack
  // key is present (default 0) so segments stay aligned across days.
  const rows = data.series.map((s) => {
    const row: Record<string, number | string> = { name: formatDayLabel(s.date) };
    for (const k of keys) row[k.key] = s.counts[k.key] ?? 0;
    return row;
  });
  const labelStep = Math.max(1, Math.floor(rows.length / 8));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--line)" strokeOpacity={0.6} />
        <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: 'var(--line)' }} tick={AXIS_TICK} interval={labelStep - 1} />
        <YAxis tickLine={false} axisLine={false} width={40} allowDecimals={false} tick={AXIS_TICK} tickFormatter={formatAxisTick} />
        <Tooltip cursor={{ fill: 'var(--paper-2)' }} content={<StackTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
        {keys.map((k, i) => (
          <Bar key={k.key} dataKey={k.key} name={k.label} stackId={grouped ? undefined : stackId} fill={k.color ?? naturalColor(i)} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
